import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Printer, Search, Sparkles } from 'lucide-react';
import ExecutionLayerRadial from './components/ExecutionLayerRadial.jsx';
import BrandHeader from './components/BrandHeader.jsx';

const TABS = [
  { id: 'mri', label: 'MRI' },
  { id: 'story', label: 'Storyboard' },
  { id: 'prio', label: 'Priorities' },
  { id: 'friction', label: 'Friction' },
  { id: 'readiness', label: 'Readiness' },
  { id: 'blueprint', label: '30/60/90' },
];

function b64Encode(obj) {
  const json = JSON.stringify(obj);
  return btoa(unescape(encodeURIComponent(json)))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
}
function b64Decode(str) {
  const s = str.replaceAll('-', '+').replaceAll('_', '/');
  const pad = s + '==='.slice((s.length + 3) % 4);
  const json = decodeURIComponent(escape(atob(pad)));
  return JSON.parse(json);
}

function loadSession() {
  // Priority: URL hash (#s=...) then localStorage
  const hash = window.location.hash || '';
  const m = hash.match(/s=([^&]+)/);
  if (m?.[1]) {
    try {
      const decoded = b64Decode(m[1]);
      return decoded;
    } catch (e) {
      console.warn('Failed to decode session from URL', e);
    }
  }
  try {
    const raw = localStorage.getItem('el_session_v1');
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    selectedSymptomIds: [],
    pinnedFunctionIds: [],
    readiness: {},
    notes: {},
  };
}

function saveSession(session) {
  try {
    localStorage.setItem('el_session_v1', JSON.stringify(session));
  } catch {}
}

function scoreFunctions(framework, session) {
  const selectedSet = new Set(session.selectedSymptomIds);
  const scores = new Map();

  for (const s of framework.symptoms) {
    if (!selectedSet.has(s.id)) continue;
    scores.set(s.functionId, (scores.get(s.functionId) || 0) + 1);
  }

  // boost cross-functional a bit
  for (const fn of framework.functions) {
    const base = scores.get(fn.id) || 0;
    let boost = 0;
    if (fn.isCrossFunctional) boost += 0.5;
    if (fn.crossFunctionalSpan?.length) boost += Math.min(0.5, fn.crossFunctionalSpan.length * 0.08);

    // incorporate readiness penalty if they already "good"
    const r = session.readiness?.[fn.id];
    let readinessPenalty = 0;
    if (r?.confidence >= 4 && r?.people >= 4 && r?.tools >= 4 && r?.data >= 4) {
      readinessPenalty = 0.5;
    }

    const total = Math.max(0, base + boost - readinessPenalty);
    if (total > 0) scores.set(fn.id, total);
  }

  const ranked = framework.functions
    .map((fn) => ({ fn, score: scores.get(fn.id) || 0 }))
    .sort((a, b) => b.score - a.score);

  return ranked;
}

function splitList(items) {
  return (items || []).filter(Boolean);
}

function FunctionCard({ fn, score, onPin, pinned }) {
  return (
    <div className="item">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 700 }}>{fn.id} - {fn.name}</div>
          <div className="small">{fn.categoryName} · {fn.functionType}{fn.isCrossFunctional ? ' · Cross-functional' : ''}</div>
        </div>
        <div className="row">
          {score > 0 && <span className="pill"><span className="kpi">{score.toFixed(1)}</span><span className="small">score</span></span>}
          <button className={"btn " + (pinned ? 'primary' : '')} onClick={onPin}>{pinned ? 'Pinned' : 'Pin'}</button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [framework, setFramework] = useState(null);
  const [tab, setTab] = useState('mri');
  const [session, setSession] = useState(() => loadSession());
  const [search, setSearch] = useState('');
  const [activeFunctionId, setActiveFunctionId] = useState(null);

  useEffect(() => {
    fetch('/framework.json')
      .then((r) => r.json())
      .then((j) => {
        setFramework(j);
        if (!activeFunctionId && j.functions?.length) setActiveFunctionId(j.functions[0].id);
      })
      .catch((e) => console.error('Failed to load framework.json', e));
  }, []);

  useEffect(() => {
    saveSession(session);
  }, [session]);

  const ranked = useMemo(() => {
    if (!framework) return [];
    return scoreFunctions(framework, session);
  }, [framework, session]);

  const fnById = useMemo(() => {
    if (!framework) return new Map();
    return new Map(framework.functions.map((f) => [f.id, f]));
  }, [framework]);

  const activeFn = activeFunctionId ? fnById.get(activeFunctionId) : null;

  const shareUrl = useMemo(() => {
    const encoded = b64Encode(session);
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}#s=${encoded}`;
  }, [session]);

  function copyShare() {
    navigator.clipboard?.writeText(shareUrl);
  }

  function togglePin(fnId) {
    setSession((s) => {
      const set = new Set(s.pinnedFunctionIds);
      if (set.has(fnId)) set.delete(fnId); else set.add(fnId);
      return { ...s, pinnedFunctionIds: Array.from(set) };
    });
  }

  function addTopToStoryboard(n=3) {
    if (!framework) return;
    const top = ranked.filter((r) => r.score > 0).slice(0, n).map((r) => r.fn.id);
    if (!top.length) return;
    setSession((s) => {
      const set = new Set(s.pinnedFunctionIds);
      top.forEach((id) => set.add(id));
      return { ...s, pinnedFunctionIds: Array.from(set) };
    });
    setTab('story');
  }

  if (!framework) {
    return (
      <div className="container">
        <div className="panel">
          <h2>Loading</h2>
          <div className="small">Loading framework.json… (run <code>npm run dev</code> after install)</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="noPrint">
        <BrandHeader
          subtitle={`Execution Layer Tools, v${framework.meta?.version} · ${framework.functions.length} functions · ${framework.symptoms.length} symptoms`}
          right={
            <>
              <button className="iconBtn" onClick={copyShare} title="Copy share link"><Copy size={18} /></button>
              <button className="iconBtn" onClick={() => window.print()} title="Print / Save PDF"><Printer size={18} /></button>
            </>
          }
        />
      </div>

      <div className="printOnly">
        <BrandHeader subtitle={`Execution Layer Tools, v${framework.meta?.version}`} right={null} />
      </div>

      <div className="tabs noPrint">
        {TABS.map((t) => (
          <button key={t.id} className={"tab " + (tab === t.id ? 'active' : '')} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid">
        <div className="panel">
          <h2>{TABS.find((t) => t.id === tab)?.label}</h2>

          {tab === 'mri' && (
            <>
              <div className="row" style={{ marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div className="row">
                    <Search size={16} />
                    <input className="input" placeholder="Search symptoms (from Before States)…" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  <div className="small" style={{ marginTop: 6 }}>
                    Select symptoms. The map and ranking update instantly.
                  </div>
                </div>
                <button className="btn primary" onClick={() => addTopToStoryboard(3)}><Sparkles size={16} /> Add Top 3 to Storyboard</button>
              </div>

              <div className="panel" style={{ padding: 0, border: 'none', background: 'transparent' }}>
                <ExecutionLayerRadial
                  functions={framework.functions}
                  scores={new Map(ranked.map((r) => [r.fn.id, r.score]))}
                  activeFunctionId={activeFunctionId}
                  onSelectFunction={setActiveFunctionId}
                />
              </div>

              <div className="hr" />

              <div className="list" style={{ maxHeight: 340, overflow: 'auto', paddingRight: 4 }}>
                {framework.symptoms
                  .filter((s) => !search || s.label.toLowerCase().includes(search.toLowerCase()) || s.functionId.toLowerCase().includes(search.toLowerCase()))
                  .slice(0, 180)
                  .map((sym) => {
                    const checked = session.selectedSymptomIds.includes(sym.id);
                    const fn = fnById.get(sym.functionId);
                    return (
                      <label key={sym.id} className="item symptomItem">
                        <span className={'checkbox ' + (checked ? 'checked' : '')} aria-hidden="true">
                          {checked ? '✓' : ''}
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setSession((s) => {
                              const set = new Set(s.selectedSymptomIds);
                              if (set.has(sym.id)) set.delete(sym.id); else set.add(sym.id);
                              return { ...s, selectedSymptomIds: Array.from(set) };
                            });
                          }}
                          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                        />
                        <div>
                          <div style={{ fontWeight: 700 }}>{sym.label}</div>
                          <div className="symptomMeta">
                            <span className="pill symptomFnPill"><strong>{fn?.id}</strong></span>
                            <span className="small">{fn?.name}</span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
              </div>

              <div className="small" style={{ marginTop: 8 }}>
                Showing first 180 matches for speed. Refine search to find anything.
              </div>
            </>
          )}

          {tab === 'story' && (
            <>
              <div className="small noPrint" style={{ marginBottom: 10 }}>
                Pinned functions become your client-ready story. Use Print to Save PDF.
              </div>

              <div className="list">
                {session.pinnedFunctionIds.length === 0 && (
                  <div className="item">
                    <div style={{ fontWeight: 700 }}>No pinned functions yet</div>
                    <div className="small">Go to MRI and click “Add Top 3 to Storyboard” or pin functions manually.</div>
                  </div>
                )}

                {session.pinnedFunctionIds.map((id) => {
                  const fn = fnById.get(id);
                  if (!fn) return null;
                  return (
                    <div key={id} className="item">
                      <div className="row" style={{ justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 16 }}>{fn.id} - {fn.fullName}</div>
                          <div className="small">{fn.categoryName} · {fn.functionType}{fn.isCrossFunctional ? ' · Cross-functional' : ''}</div>
                        </div>
                        <div className="row noPrint">
                          <button className="btn" onClick={() => setActiveFunctionId(fn.id)}>View</button>
                          <button className="btn" onClick={() => togglePin(fn.id)}>Remove</button>
                        </div>
                      </div>

                      <div className="hr" />

                      <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div className="pill">Today (Before)</div>
                          <ul>
                            {splitList(fn.beforeStates).slice(0, 8).map((t, i) => <li key={i}>{t}</li>)}
                          </ul>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="pill">Future (After)</div>
                          <ul>
                            {splitList(fn.afterStates).slice(0, 8).map((t, i) => <li key={i}>{t}</li>)}
                          </ul>
                        </div>
                      </div>

                      <div className="small"><b>Flow:</b> {fn.flowDescription || '-'}</div>

                      <div className="row" style={{ marginTop: 8, alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div className="pill">People</div>
                          <div className="small">{(fn.inputs?.people || []).slice(0, 8).join(' · ') || '-'}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="pill">Tools</div>
                          <div className="small">{(fn.inputs?.technology || []).slice(0, 8).join(' · ') || '-'}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div className="pill">Data</div>
                          <div className="small">{(fn.inputs?.data || []).slice(0, 8).join(' · ') || '-'}</div>
                        </div>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <div className="pill">Outputs</div>
                        <div className="small">{(fn.outputs || []).slice(0, 10).join(' · ') || '-'}</div>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <div className="pill">Evolution</div>
                        <div className="small"><b>IP:</b> {fn.evolution?.ip || '-'}</div>
                        <div className="small"><b>Product:</b> {fn.evolution?.product || '-'}</div>
                        <div className="small"><b>Data:</b> {fn.evolution?.data || '-'}</div>
                        <div className="small"><b>AI:</b> {fn.evolution?.ai || '-'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {tab === 'prio' && (
            <PrioritiesView framework={framework} session={session} ranked={ranked} onPin={togglePin} />
          )}

          {tab === 'friction' && (
            <FrictionView framework={framework} session={session} activeFn={activeFn} onSelectFn={setActiveFunctionId} />
          )}

          {tab === 'readiness' && (
            <ReadinessView framework={framework} session={session} activeFn={activeFn} onUpdateSession={setSession} />
          )}

          {tab === 'blueprint' && (
            <BlueprintView framework={framework} session={session} ranked={ranked} />
          )}
        </div>

        <div className="panel">
          <h2>Function Explorer</h2>

          <div className="noPrint" style={{ marginBottom: 10 }}>
            <select className="select" value={activeFunctionId || ''} onChange={(e) => setActiveFunctionId(e.target.value)}>
              {framework.functions.map((f) => (
                <option key={f.id} value={f.id}>{f.id} - {f.name}</option>
              ))}
            </select>
          </div>

          {activeFn ? (
            <>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 800 }}>{activeFn.id} - {activeFn.fullName}</div>
                  <div className="small">{activeFn.categoryName} · {activeFn.functionType}{activeFn.isCrossFunctional ? ' · Cross-functional' : ''}</div>
                </div>
                <button className={"btn " + (session.pinnedFunctionIds.includes(activeFn.id) ? 'primary' : '')} onClick={() => togglePin(activeFn.id)}>
                  {session.pinnedFunctionIds.includes(activeFn.id) ? 'Pinned' : 'Pin'}
                </button>
              </div>

              <div className="hr" />

              <div className="small"><b>Cross-functional span:</b> {activeFn.crossFunctionalSpanRaw || '-'}</div>
              <div className="small"><b>Applies to:</b> {activeFn.appliesToRaw || 'All'}</div>
              <div className="small" style={{ marginTop: 8 }}><b>Flow:</b> {activeFn.flowDescription || '-'}</div>

              <div className="hr" />

              <div className="pill">Before</div>
              <ul>
                {splitList(activeFn.beforeStates).slice(0, 8).map((t, i) => <li key={i}>{t}</li>)}
              </ul>

              <div className="pill">After</div>
              <ul>
                {splitList(activeFn.afterStates).slice(0, 8).map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </>
          ) : (
            <div className="small">Select a function</div>
          )}

          <div className="hr" />

          <div className="small printOnly">
            Share link: {shareUrl}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrioritiesView({ framework, session, ranked, onPin }) {
  const top = ranked.filter((r) => r.score > 0);

  function bandFor(fn, score) {
    const r = session.readiness?.[fn.id];
    const stage = (r?.stage || '').toLowerCase();
    const readiness = (r?.confidence || 0) + (r?.people || 0) + (r?.tools || 0) + (r?.data || 0);

    if (score >= 3 && readiness < 14) return 'Now';
    if (score >= 2) return 'Next';
    if (score > 0) return 'Later';
    // no signal: keep out
    return null;
  }

  const buckets = { Now: [], Next: [], Later: [] };
  for (const r of top) {
    const band = bandFor(r.fn, r.score);
    if (band) buckets[band].push(r);
  }

  return (
    <>
      <div className="small" style={{ marginBottom: 10 }}>
        This is the “4A” prioritization view: banded by MRI signal, cross-functional boost, and readiness.
      </div>

      {Object.entries(buckets).map(([name, items]) => (
        <div key={name} style={{ marginBottom: 12 }}>
          <div className="pill"><b>{name}</b> · {items.length}</div>
          <div className="list" style={{ marginTop: 8 }}>
            {items.length === 0 ? (
              <div className="item"><div className="small">No items</div></div>
            ) : (
              items.slice(0, 8).map(({ fn, score }) => (
                <FunctionCard
                  key={fn.id}
                  fn={fn}
                  score={score}
                  pinned={session.pinnedFunctionIds.includes(fn.id)}
                  onPin={() => onPin(fn.id)}
                />
              ))
            )}
          </div>
        </div>
      ))}

      <div className="small">Tip: set Readiness scores to make bands more accurate.</div>
    </>
  );
}

function FrictionView({ framework, session, activeFn, onSelectFn }) {
  const crossFns = framework.functions.filter((f) => f.isCrossFunctional);
  const fn = (activeFn && activeFn.isCrossFunctional) ? activeFn : crossFns[0];

  const depts = fn?.crossFunctionalSpan || [];
  const edges = depts.map((d, i) => (i < depts.length - 1 ? [d, depts[i + 1]] : null)).filter(Boolean);

  return (
    <>
      <div className="small" style={{...{ marginBottom: 10 }}}>
        Cross-functional friction map (MVP): departments involved + a draft handoff chain.
      </div>

      <div className="noPrint" style={{ marginBottom: 10 }}>
        <select className="select" value={fn?.id || ''} onChange={(e) => onSelectFn(e.target.value)}>
          {crossFns.map((f) => <option key={f.id} value={f.id}>{f.id} - {f.name}</option>)}
        </select>
      </div>

      <div className="item">
        <div style={{ fontWeight: 800 }}>{fn.id} - {fn.fullName}</div>
        <div className="small">Span: {fn.crossFunctionalSpanRaw || '-'}</div>
      </div>

      <div className="hr" />

      <div className="pill">Departments</div>
      <div className="row" style={{ marginTop: 8 }}>
        {depts.length ? depts.map((d) => <span key={d} className="pill">{d}</span>) : <span className="small">-</span>}
      </div>

      <div className="hr" />

      <div className="pill">Draft Handoffs</div>
      <div className="list" style={{ marginTop: 8 }}>
        {edges.length ? edges.map((e, i) => (
          <div key={i} className="item">
            <div style={{ fontWeight: 650 }}>{e[0]} → {e[1]}</div>
            <div className="small">Common failure mode: unclear ownership, missing data, exceptions handled in Slack/email.</div>
          </div>
        )) : <div className="item"><div className="small">No edges</div></div>}
      </div>

      <div className="hr" />

      <div className="pill">Draft RACI (starter)</div>
      <div className="item">
        <div className="small"><b>R</b> (Responsible): primary operator for {fn.name}</div>
        <div className="small"><b>A</b> (Accountable): exec owner across departments</div>
        <div className="small"><b>C</b> (Consulted): all departments in span</div>
        <div className="small"><b>I</b> (Informed): downstream teams impacted</div>
      </div>
    </>
  );
}

function ReadinessView({ framework, session, activeFn, onUpdateSession }) {
  const fn = activeFn || framework.functions[0];
  const r = session.readiness?.[fn.id] || { stage: 'IP', people: 2, tools: 2, data: 2, confidence: 2 };

  function update(patch) {
    onUpdateSession((s) => ({
      ...s,
      readiness: {
        ...s.readiness,
        [fn.id]: { ...r, ...patch }
      }
    }));
  }

  const stage = (r.stage || 'IP').toUpperCase();
  const nextMoves = {
    IP: ['Define the workflow + decision rights', 'Document exceptions', 'Pick 3 KPIs + owners'],
    PRODUCT: ['Standardize process in tools', 'Remove re-keying', 'Implement controls + audit trail'],
    DATA: ['Instrument events', 'Define data model', 'Build dashboards + alerts'],
    AI: ['Deploy agents with escalation paths', 'Human-in-the-loop rules', 'Monitor drift + exceptions']
  }[stage] || [];

  return (
    <>
      <div className="item">
        <div style={{ fontWeight: 800 }}>{fn.id} - {fn.fullName}</div>
        <div className="small">Set readiness and stage. This influences prioritization.</div>
      </div>

      <div className="hr" />

      <div className="row" style={{ gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="small">Stage</div>
          <select className="select" value={r.stage} onChange={(e) => update({ stage: e.target.value })}>
            <option value="IP">IP</option>
            <option value="Product">Product</option>
            <option value="Data">Data</option>
            <option value="AI">AI</option>
          </select>
        </div>
        {['people','tools','data','confidence'].map((k) => (
          <div key={k} style={{ flex: 1 }}>
            <div className="small">{k}</div>
            <select className="select" value={r[k]} onChange={(e) => update({ [k]: Number(e.target.value) })}>
              {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div className="hr" />

      <div className="pill">Next best moves</div>
      <div className="list" style={{ marginTop: 8 }}>
        {nextMoves.map((m, i) => (
          <div key={i} className="item"><div style={{ fontWeight: 650 }}>{m}</div></div>
        ))}
      </div>

      <div className="hr" />

      <div className="pill">Evolution detail (from framework)</div>
      <div className="item"><div className="small"><b>IP:</b> {fn.evolution?.ip || '-'}</div></div>
      <div className="item"><div className="small"><b>Product:</b> {fn.evolution?.product || '-'}</div></div>
      <div className="item"><div className="small"><b>Data:</b> {fn.evolution?.data || '-'}</div></div>
      <div className="item"><div className="small"><b>AI:</b> {fn.evolution?.ai || '-'}</div></div>
    </>
  );
}

function BlueprintView({ framework, session, ranked }) {
  const pinned = session.pinnedFunctionIds.map((id) => framework.functions.find((f) => f.id === id)).filter(Boolean);
  const top = ranked.filter((r) => r.score > 0).slice(0, 5).map((r) => r.fn);
  const chosen = (pinned.length ? pinned : top).slice(0, 5);

  const md = useMemo(() => {
    const lines = [];
    lines.push(`# 30/60/90 Blueprint`);
    lines.push('');
    lines.push(`Generated from symptoms + readiness + cross-functional boost.`);
    lines.push('');
    lines.push(`## Focus Functions`);
    for (const fn of chosen) {
      lines.push(`- **${fn.id} - ${fn.fullName}** (${fn.categoryName}${fn.isCrossFunctional ? ', cross-functional' : ''})`);
    }
    lines.push('');

    const phase = (title, items) => {
      lines.push(`## ${title}`);
      lines.push('');
      items.forEach((t) => lines.push(`- ${t}`));
      lines.push('');
    };

    const fnNames = chosen.map((f) => f.name).join(', ');
    phase('Days 0–30: Diagnose + stabilize', [
      `Run a structured diagnostic on: ${fnNames}.`,
      'Document exceptions (the stuff that breaks automation).',
      'Assign owners + decision rights (draft RACI).',
      'Pick 3 KPIs per focus function and define measurement cadence.',
      'Ship 1–2 quick wins that remove re-keying or manual reconciliation.'
    ]);

    phase('Days 31–60: Design + standardize', [
      'Map the target workflow (“After” state) and align tools to it.',
      'Create a minimum data model + event capture for the workflow.',
      'Implement controls / audit trail for critical decisions.',
      'Train teams on the new operating rhythm (weekly reviews).' 
    ]);

    phase('Days 61–90: Data → AI (only where ready)', [
      'Build dashboards + alerts around the workflow KPIs.',
      'Automate repetitive steps and route exceptions to owners.',
      'If AI is in scope: deploy human-in-the-loop rules + escalation paths.',
      'Create a “Stay” cadence: monthly operating review + backlog refresh.'
    ]);

    lines.push('## Notes');
    lines.push('');
    lines.push('- This is a first-pass blueprint. Validate assumptions with real metrics and system constraints.');
    return lines.join('\n');
  }, [framework.functions, session, ranked]);

  return (
    <>
      <div className="small" style={{ marginBottom: 10 }}>
        Blueprint is generated from pinned functions (preferred) or the top MRI signals.
      </div>

      <div className="item">
        <div style={{ fontWeight: 700 }}>Selected functions ({chosen.length})</div>
        <div className="small">{chosen.map((f) => `${f.id} ${f.name}`).join(' · ') || '-'}</div>
      </div>

      <div className="hr" />

      <textarea className="input" style={{ minHeight: 420, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }} value={md} readOnly />

      <div className="small" style={{ marginTop: 8 }}>
        Tip: Print → Save PDF to share. The share link preserves your session.
      </div>
    </>
  );
}
